import React, { useEffect, useState, useRef } from 'react';
import { Loader2, Upload, X, Copy, Check, Trash2, FileText, Image as ImageIcon, ExternalLink, Search, FolderOpen } from 'lucide-react';
import api, { API_BASE, resolveImg } from '../../lib/api';
import { useToast } from '../../hooks/use-toast';

function formatBytes(n) {
  if (!n) return '0 B';
  const u = ['B', 'KB', 'MB'];
  let i = 0;
  while (n >= 1024 && i < u.length - 1) { n /= 1024; i++; }
  return `${n.toFixed(i === 0 ? 0 : 1)} ${u[i]}`;
}

function formatDate(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return ''; }
}

export default function AdminMedia() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [kind, setKind] = useState('all');
  const [q, setQ] = useState('');
  const [selected, setSelected] = useState(null); // media item open in modal
  const [copied, setCopied] = useState(false);
  const [usage, setUsage] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const fileRef = useRef(null);
  const { toast } = useToast();

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (kind !== 'all') params.kind = kind;
      if (q.trim()) params.q = q.trim();
      const { data } = await api.get('/admin/media', { params });
      setItems(data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [kind]);

  const onUpload = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    fd.append('folder', 'general');
    try {
      setUploading(true);
      await api.post('/admin/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast({ title: 'Subido correctamente' });
      load();
    } catch (err) {
      toast({ title: 'Error al subir', description: err.response?.data?.detail || 'Fallo desconocido', variant: 'destructive' });
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const openItem = async (it) => {
    setSelected(it);
    setUsage(null);
    setCopied(false);
    try {
      const { data } = await api.get(`/admin/media/${it.id}/usage`);
      setUsage(data);
    } catch { setUsage({ usages: [], count: 0 }); }
  };

  const copyUrl = (it) => {
    const full = `${window.location.origin}${it.url}`;
    navigator.clipboard.writeText(full);
    setCopied(true);
    toast({ title: 'URL copiada', description: full });
    setTimeout(() => setCopied(false), 2000);
  };

  const remove = async (it) => {
    if (!window.confirm(`¿Borrar "${it.filename}"? Solo se puede borrar si no está en uso.`)) return;
    try {
      await api.delete(`/admin/media/${it.id}`);
      toast({ title: 'Archivo borrado' });
      setSelected(null);
      load();
    } catch (err) {
      toast({ title: 'No se puede borrar', description: err.response?.data?.detail || 'Error', variant: 'destructive' });
    }
  };

  const importExternal = async () => {
    if (!window.confirm('Vamos a buscar imágenes externas (Pexels, Unsplash…) en tu contenido, descargarlas y guardarlas en Media. Puede tardar 30-60s. ¿Continuar?')) return;
    try {
      setImporting(true);
      const { data } = await api.post('/admin/media/import-external');
      setImportResult(data);
      toast({ title: 'Importación completada', description: `${data.downloaded} descargadas, ${data.rewritten} referencias actualizadas` });
      load();
    } catch (err) {
      toast({ title: 'Error importando', description: err.response?.data?.detail || 'Fallo', variant: 'destructive' });
    } finally {
      setImporting(false);
    }
  };

  const filtered = items.filter((it) => !q.trim() || it.filename.toLowerCase().includes(q.trim().toLowerCase()));

  return (
    <div className="p-6 md:p-10" data-testid="admin-media-page">
      <div className="flex items-center justify-between mb-2 flex-wrap gap-3">
        <div>
          <h1 className="font-serif-display text-3xl text-white mb-1">Media</h1>
          <p className="text-white/55 text-sm">Todos los archivos subidos (imágenes y PDFs). Sube, copia URLs y elimina.</p>
        </div>
        <div className="flex gap-3 flex-wrap">
          <button
            onClick={importExternal}
            disabled={importing || uploading}
            className="px-4 py-2.5 text-[11px] uppercase tracking-[0.3em] font-medium border border-[#c9a063]/60 text-[#c9a063] hover:bg-[#c9a063]/10 inline-flex items-center gap-2 disabled:opacity-60"
            title="Descarga y guarda todas las imágenes externas (Pexels, Unsplash…) que están enlazadas en tu web"
            data-testid="admin-media-import-external-btn"
          >
            {importing ? <Loader2 size={14} className="animate-spin" /> : <FolderOpen size={14} />}
            {importing ? 'Importando...' : 'Importar externas'}
          </button>
          <input ref={fileRef} type="file" accept=".jpg,.jpeg,.png,.webp,.pdf" onChange={onUpload} className="hidden" data-testid="admin-media-file-input" />
          <button
            onClick={() => fileRef.current && fileRef.current.click()}
            disabled={uploading}
            className="gd-btn-gold px-5 py-2.5 text-[11px] uppercase tracking-[0.3em] font-medium inline-flex items-center gap-2 disabled:opacity-60"
            data-testid="admin-media-upload-btn"
          >
            {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
            {uploading ? 'Subiendo...' : 'Subir archivo'}
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="mt-6 flex flex-wrap items-center gap-3">
        <div className="flex gap-1 border border-white/10 p-0.5">
          {[{ v: 'all', l: 'Todos' }, { v: 'image', l: 'Imágenes' }, { v: 'pdf', l: 'PDFs' }].map((k) => (
            <button
              key={k.v}
              onClick={() => setKind(k.v)}
              className={`px-3 py-1.5 text-[10px] uppercase tracking-widest ${kind === k.v ? 'bg-[#c9a063] text-black' : 'text-white/60 hover:text-white'}`}
              data-testid={`admin-media-filter-${k.v}`}
            >
              {k.l}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2 bg-zinc-950 border border-white/10 px-2.5 py-1.5 flex-1 max-w-xs">
          <Search size={13} className="text-white/40" />
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar por nombre..."
            className="bg-transparent text-white/90 text-sm focus:outline-none w-full"
            data-testid="admin-media-search"
          />
        </div>
        <span className="text-white/40 text-xs ml-auto">{filtered.length} {filtered.length === 1 ? 'archivo' : 'archivos'}</span>
      </div>

      {/* Grid */}
      <div className="mt-6">
        {loading ? (
          <Loader2 className="animate-spin text-[#c9a063]" />
        ) : filtered.length === 0 ? (
          <div className="gd-card p-12 text-center text-white/40" data-testid="admin-media-empty">
            No hay archivos. Pulsa &quot;Subir archivo&quot; para añadir el primero.
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
            {filtered.map((it) => (
              <button
                key={it.id}
                onClick={() => openItem(it)}
                className="group relative aspect-square bg-zinc-900 border border-white/5 hover:border-[#c9a063] overflow-hidden transition-colors text-left"
                data-testid={`admin-media-item-${it.id}`}
              >
                {it.kind === 'image' ? (
                  <img src={resolveImg(it.url)} alt={it.filename} className="w-full h-full object-cover" loading="lazy" />
                ) : (
                  <div className="w-full h-full flex flex-col items-center justify-center text-white/40 p-3">
                    <FileText size={40} />
                    <span className="text-[10px] uppercase tracking-widest mt-2">PDF</span>
                  </div>
                )}
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/90 to-transparent p-2 opacity-0 group-hover:opacity-100 transition-opacity">
                  <div className="text-white text-[11px] truncate">{it.filename}</div>
                  <div className="text-white/60 text-[10px]">{formatBytes(it.size)}</div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Modal */}
      {selected && (
        <div className="fixed inset-0 bg-black/85 z-50 flex items-center justify-center p-4" onClick={() => setSelected(null)} data-testid="admin-media-modal">
          <div className="bg-[#0d0d0e] border border-white/10 max-w-3xl w-full max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b border-white/10">
              <div className="flex items-center gap-2 text-[#c9a063]">
                {selected.kind === 'image' ? <ImageIcon size={16} /> : <FileText size={16} />}
                <span className="font-serif-display text-lg text-white truncate max-w-md">{selected.filename}</span>
              </div>
              <button onClick={() => setSelected(null)} className="text-white/60 hover:text-white" data-testid="admin-media-modal-close"><X size={18} /></button>
            </div>

            <div className="p-5">
              <div className="bg-zinc-950 border border-white/5 flex items-center justify-center min-h-[240px] max-h-[420px] overflow-hidden">
                {selected.kind === 'image' ? (
                  <img src={resolveImg(selected.url)} alt={selected.filename} className="max-w-full max-h-[420px] object-contain" />
                ) : (
                  <div className="flex flex-col items-center gap-3 py-16 text-white/60">
                    <FileText size={64} />
                    <a href={resolveImg(selected.url)} target="_blank" rel="noreferrer" className="text-[#c9a063] hover:underline text-sm inline-flex items-center gap-2">
                      Abrir PDF <ExternalLink size={13} />
                    </a>
                  </div>
                )}
              </div>

              <div className="mt-5 grid grid-cols-2 md:grid-cols-4 gap-3 text-[11px]">
                <div><div className="text-white/40 uppercase tracking-widest">Tamaño</div><div className="text-white/90 mt-1">{formatBytes(selected.size)}</div></div>
                <div><div className="text-white/40 uppercase tracking-widest">Tipo</div><div className="text-white/90 mt-1">{selected.ext || (selected.kind === 'image' ? 'Imagen' : 'PDF')}</div></div>
                <div><div className="text-white/40 uppercase tracking-widest">Carpeta</div><div className="text-white/90 mt-1">{selected.folder || 'general'}</div></div>
                <div><div className="text-white/40 uppercase tracking-widest">Subido</div><div className="text-white/90 mt-1">{formatDate(selected.created_at)}</div></div>
              </div>

              <div className="mt-4 bg-black/40 border border-white/5 p-3">
                <div className="text-[10px] uppercase tracking-widest text-white/40 mb-1.5">URL pública</div>
                <div className="flex items-center gap-2">
                  <code className="text-xs text-[#c9a063] break-all flex-1">{`${window.location.origin}${selected.url}`}</code>
                  <button onClick={() => copyUrl(selected)} className="px-3 py-1.5 border border-[#c9a063] text-[#c9a063] text-[10px] uppercase tracking-widest inline-flex items-center gap-1.5 hover:bg-[#c9a063]/10" data-testid="admin-media-modal-copy">
                    {copied ? <Check size={12} /> : <Copy size={12} />} {copied ? 'Copiado' : 'Copiar'}
                  </button>
                </div>
              </div>

              {/* Usage */}
              <div className="mt-4">
                <div className="text-[10px] uppercase tracking-widest text-white/40 mb-2">En uso en</div>
                {!usage ? (
                  <div className="text-white/50 text-xs inline-flex items-center gap-2"><Loader2 size={11} className="animate-spin" /> Comprobando...</div>
                ) : usage.count === 0 ? (
                  <div className="text-emerald-400/80 text-xs">No está en uso — se puede borrar sin romper nada.</div>
                ) : (
                  <ul className="space-y-1">
                    {usage.usages.map((u, i) => (
                      <li key={i} className="text-white/70 text-xs flex items-center gap-2">
                        <span className="px-1.5 py-0.5 border border-white/15 text-[9px] uppercase tracking-widest text-[#c9a063]">{u.collection}</span>
                        <span className="truncate">{u.title}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div className="mt-6 flex justify-between items-center border-t border-white/10 pt-4">
                <a href={resolveImg(selected.url)} target="_blank" rel="noreferrer" className="text-xs text-white/60 hover:text-[#c9a063] inline-flex items-center gap-1.5" data-testid="admin-media-modal-open">
                  <ExternalLink size={12} /> Abrir en pestaña nueva
                </a>
                <button
                  onClick={() => remove(selected)}
                  disabled={usage && usage.count > 0}
                  className="text-xs px-4 py-2 border border-red-500/50 text-red-400 hover:bg-red-500/10 inline-flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed"
                  title={usage && usage.count > 0 ? 'No se puede borrar: está en uso' : 'Borrar archivo'}
                  data-testid="admin-media-modal-delete"
                >
                  <Trash2 size={12} /> Borrar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
